import type { ComponentProps, ReactNode } from 'react'
import { useCallback, useMemo } from 'react'

import InfoIcon from '@/assets/icons/icon-info.svg'
import { Tooltip } from '@/components/ui/tooltip.tsx'
import { useMessageEditor } from '@/modules/message/components/editor/message-editor-context'
import type { PlaceholderVariant } from '@/modules/message/components/placeholder/variants'
import { classNames } from '@/utils/helpers'

export type Inserter = (variant: PlaceholderVariant) => void

export type AddMessageLabelsProps = Omit<ComponentProps<'div'>, 'children'> & {
  children: ({ insert }: { insert: Inserter }) => ReactNode
}

export const MessageLabelInserter = (props: AddMessageLabelsProps) => {
  const { children, className, ...rest } = props
  const { insert } = useMessageEditor()

  const handleInsert = useCallback(
    (variant: PlaceholderVariant) => {
      insert(variant)
    },
    [insert],
  )

  const labels = useMemo(
    () => children({ insert: handleInsert }),
    [children, handleInsert],
  )

  return (
    <div
      {...rest}
      className={classNames('flex flex-col gap-1.5', className)}
    >
      <label className="font-medium text-sm text-text-default flex items-center gap-2">
        Add Label
        <Tooltip
          content="Add labels like {Fan Name} or {Custom Name} to personalize your messages. Labels are automatically replaced with fan details when messages are sent."
          contentClassName="max-w-[280px] text-left"
        >
          <InfoIcon className="text-secondary-300 h-4 w-4" />
        </Tooltip>
      </label>
      <div className="relative flex items-center flex-grow gap-3">{labels}</div>
    </div>
  )
}
